import { useEffect, useMemo, useState, useRef } from 'react';
import './App.css';

const API_BASE_URL = 'https://dev.getadmira.com';

const FALLBACK_HEAR_ABOUT_OPTIONS = [
  { value: 1, label: 'Friend or colleague referral' },
  { value: 2, label: 'Social media' },
  { value: 3, label: 'Google search' },
  { value: 4, label: 'YouTube' },
  { value: 5, label: 'Online community' },
  { value: 6, label: 'Event or webinar' },
  { value: 18, label: 'Other' },
];

// Real options from admira-frontend signup flow (SignupAddInfo.tsx).
const MAIN_GOAL_OPTIONS = [
  { value: 36, label: 'Unified Data Dashboard' },
  { value: 37, label: 'Real-Time Performance Insights' },
  { value: 38, label: 'Comprehensive Multi-Touch Attribution' },
  { value: 39, label: 'Privacy-Resilient Tracking' },
  { value: 40, label: 'Data-Driven ROI Optimization' },
  { value: 41, label: 'Customizable Reporting' },
  { value: 42, label: 'Seamless Integrations' },
  { value: 43, label: 'Actionable Analytics' },
  { value: 44, label: 'Cross-Channel Visibility' },
  { value: 45, label: 'Scalable Solutions' },
];

const FALLBACK_INDUSTRY_OPTIONS = [
  { value: 1, label: 'Marketing and Advertising' },
  { value: 2, label: 'E-commerce' },
  { value: 3, label: 'SaaS' },
  { value: 4, label: 'Education' },
  { value: 5, label: 'Healthcare' },
  { value: 6, label: 'Finance' },
  { value: 7, label: 'Other' },
];

const PLAN_OPTIONS = [
  {
    id: 'starter',
    title: 'Starter',
    monthlyPrice: 199,
    annualMonthlyPrice: 149,
    features: ['Up to 10,000 MTU', 'Basic analytics', 'Up to 1 seat'],
  },
  {
    id: 'growth',
    title: 'Growth',
    monthlyPrice: 399,
    annualMonthlyPrice: 299,
    popular: true,
    features: ['Up to 30,000 MTU', 'Basic analytics', 'Up to 5 seats'],
  },
  {
    id: 'enterprise',
    title: 'Enterprise',
    price: null,
    features: [
      'Unlimited contacts',
      'Custom BI and reporting',
      'Dedicated CSM',
      'SLA and security review',
      'Unlimited team members',
    ],
  },
];

function App() {
  const apiBaseUrl = useMemo(() => API_BASE_URL.replace(/\/+$/, ''), []);
  
  // OBSERVER FOR IFRAME
  const containerRef = useRef(null);
  useEffect(() => {
    if (!containerRef.current) return;

    const observer = new ResizeObserver((entries) => {
      for (let entry of entries) {
        // Obtenemos la altura total del contenido
        const newHeight = entry.target.scrollHeight;
        
        // Enviamos el mensaje a la landing page
        window.parent.postMessage(
          { type: 'IFRAME_RESIZE', height: newHeight }, 
          '*' // Puedes cambiar '*' por 'https://getadmira.com' por mayor seguridad
        );
      }
    });

    observer.observe(containerRef.current);

    return () => observer.disconnect();
  }, []);

  // END OF OBSERVER FOR IFRAME


  const [hearAboutOptions, setHearAboutOptions] = useState(
    FALLBACK_HEAR_ABOUT_OPTIONS
  );
  const [industryOptions, setIndustryOptions] = useState(FALLBACK_INDUSTRY_OPTIONS);
  const [formData, setFormData] = useState({
    companyName: '',
    companyDomain: '',
    industry: '',
    firstName: '',
    lastName: '',
    email: '',
    password: '',
    howDidYouHear: '',
    hearAboutOther: '',
    mainGoal: '',
    selectedPlan: 'growth',
    billingCycle: 'monthly',
    enterpriseBillingCycle: 'monthly',
    enterpriseMonthlyPrice: '',
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [message, setMessage] = useState('');
  const [error, setError] = useState('');
  const isAnnualBilling = formData.billingCycle === 'annual';

  useEffect(() => {
    const fetchParametrics = async () => {
      try {
        const [hearAboutResponse, industryResponse] = await Promise.all([
          fetch(`${apiBaseUrl}/api/parametrics/hear_about`, {
            method: 'GET',
            credentials: 'include',
          }),
          fetch(`${apiBaseUrl}/api/parametrics/industry`, {
            method: 'GET',
            credentials: 'include',
          }),
        ]);

        if (hearAboutResponse.ok) {
          const hearAboutData = await hearAboutResponse.json();
          if (Array.isArray(hearAboutData) && hearAboutData.length > 0) {
            setHearAboutOptions(
              hearAboutData.map((option) => ({
                value: option.id,
                label: option.value,
              }))
            );
          }
        }

        if (!industryResponse.ok) {
          return;
        }

        const industryData = await industryResponse.json();
        if (Array.isArray(industryData) && industryData.length > 0) {
          setIndustryOptions(
            industryData.map((option) => ({
              value: option.id,
              label: option.value,
            }))
          );
        }
      } catch (fetchError) {
        // Keep fallback options when backend parametrics cannot be reached/authenticated.
      }
    };

    fetchParametrics();
  }, [apiBaseUrl]);

  const handleChange = (event) => {
    const { name, value } = event.target;
    setFormData((previous) => ({
      ...previous,
      [name]: value,
    }));
  };

  const handleBillingCycleChange = (billingCycle) => {
    setFormData((previous) => ({
      ...previous,
      billingCycle,
    }));
  };

  const handleEnterpriseBillingCycleChange = (enterpriseBillingCycle) => {
    setFormData((previous) => ({
      ...previous,
      enterpriseBillingCycle,
    }));
  };

  const getDisplayedPlanPrice = (plan) => {
    if (plan.id === 'enterprise') {
      return null;
    }

    return isAnnualBilling ? plan.annualMonthlyPrice : plan.monthlyPrice;
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    setMessage('');
    setError('');
    setIsSubmitting(true);

    try {
      const csrfResponse = await fetch(`${apiBaseUrl}/api/csrf-token`, {
        method: 'GET',
        credentials: 'include',
      });

      if (!csrfResponse.ok) {
        throw new Error('Could not fetch CSRF token from backend.');
      }

      const csrfPayload = await csrfResponse.json();
      const csrfToken = csrfPayload.csrf_token || '';

      const normalizedEmail = formData.email.trim();

      const createUserPayload = {
        first_name: formData.firstName.trim(),
        last_name: formData.lastName.trim(),
        email_address: normalizedEmail,
        password: formData.password,
        how_did_you_hear_about_us: Number(formData.howDidYouHear),
        main_goal_with_admira: Number(formData.mainGoal),
        selected_plan: formData.selectedPlan,
      };

      const createUserResponse = await fetch(`${apiBaseUrl}/api/create-user`, {
        method: 'POST',
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json',
          'X-CSRF-Token': csrfToken,
        },
        body: JSON.stringify(createUserPayload),
      });

      const userContentType = createUserResponse.headers.get('content-type') || '';
      const createUserData = userContentType.includes('application/json')
        ? await createUserResponse.json()
        : await createUserResponse.text();

      if (!createUserResponse.ok) {
        const backendError =
          typeof createUserData === 'string'
            ? createUserData
            : createUserData?.error || 'Could not create user.';
        throw new Error(backendError);
      }

      const companyPayload = {
        name: formData.companyName.trim(),
        email: normalizedEmail,
        domain: formData.companyDomain.trim(),
        industry: Number(formData.industry),
        main_goal: Number(formData.mainGoal),
        hear_about: Number(formData.howDidYouHear),
      };

      if (Number(formData.howDidYouHear) === 18 && formData.hearAboutOther.trim()) {
        companyPayload.hear_about_other = formData.hearAboutOther.trim();
      }

      const createCompanyWorkspaceResponse = await fetch(
        `${apiBaseUrl}/api/create-company-workspace`,
        {
          method: 'POST',
          credentials: 'include',
          headers: {
            'Content-Type': 'application/json',
            'X-CSRF-Token': csrfToken,
          },
          body: JSON.stringify(companyPayload),
        }
      );

      const companyContentType =
        createCompanyWorkspaceResponse.headers.get('content-type') || '';
      const createCompanyData = companyContentType.includes('application/json')
        ? await createCompanyWorkspaceResponse.json()
        : await createCompanyWorkspaceResponse.text();

      if (!createCompanyWorkspaceResponse.ok) {
        const backendError =
          typeof createCompanyData === 'string'
            ? createCompanyData
            : createCompanyData?.error || 'Could not create company workspace.';
        throw new Error(backendError);
      }

      const enterpriseMonthlyPriceCents =
        formData.selectedPlan === 'enterprise'
          ? Math.round(Number(formData.enterpriseMonthlyPrice || '0') * 100)
          : 0;

      if (formData.selectedPlan === 'enterprise' && enterpriseMonthlyPriceCents <= 0) {
        throw new Error('Please add a valid enterprise monthly price.');
      }

      const checkoutResponse = await fetch(`${apiBaseUrl}/api/create-checkout-session`, {
        method: 'POST',
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          email: normalizedEmail,
          first_name: formData.firstName.trim(),
          last_name: formData.lastName.trim(),
          plan: formData.selectedPlan,
          billing_cycle:
            formData.selectedPlan === 'enterprise'
              ? formData.enterpriseBillingCycle
              : formData.billingCycle,
          enterprise_monthly_price_cents: enterpriseMonthlyPriceCents,
          success_url: `${window.location.origin}/?checkout=success&session_id={CHECKOUT_SESSION_ID}`,
          cancel_url: `${window.location.origin}/?checkout=cancel`,
        }),
      });

      const checkoutContentType = checkoutResponse.headers.get('content-type') || '';
      const checkoutData = checkoutContentType.includes('application/json')
        ? await checkoutResponse.json()
        : { error: await checkoutResponse.text() };

      if (!checkoutResponse.ok) {
        throw new Error(checkoutData?.error || 'Could not create Stripe checkout session.');
      }

      if (!checkoutData?.url) {
        throw new Error('Stripe checkout URL is missing in backend response.');
      }

      const createdUserId =
        typeof createUserData === 'object' && createUserData ? createUserData.id : null;
      setMessage(
        createdUserId
          ? `User and workspace created (user id: ${createdUserId}). Redirecting to Stripe checkout...`
          : 'User and workspace created. Redirecting to Stripe checkout...'
      );
      window.location.assign(checkoutData.url);
    } catch (submitError) {
      setError(submitError.message || 'Unexpected error while creating user.');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    // ref={containerRef} is for iframe functions
    <main className="App" ref={containerRef} style={{ overflow: 'hidden' }}>
      <section className="form-card">
       

        <form onSubmit={handleSubmit} className="onboarding-form">
          <label htmlFor="companyName">Company name</label>
          <input
            id="companyName"
            name="companyName"
            type="text"
            value={formData.companyName}
            onChange={handleChange}
            required
          />

          <label htmlFor="companyDomain">Company domain</label>
          <input
            id="companyDomain"
            name="companyDomain"
            type="text"
            value={formData.companyDomain}
            onChange={handleChange}
            placeholder="example.com"
            required
          />

          <label htmlFor="industry">Industry</label>
          <select
            id="industry"
            name="industry"
            value={formData.industry}
            onChange={handleChange}
            required
          >
            <option value="">Select an option</option>
            {industryOptions.map((option) => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
          </select>

          <label htmlFor="firstName">First name</label>
          <input
            id="firstName"
            name="firstName"
            type="text"
            value={formData.firstName}
            onChange={handleChange}
            required
          />

          <label htmlFor="lastName">Last name</label>
          <input
            id="lastName"
            name="lastName"
            type="text"
            value={formData.lastName}
            onChange={handleChange}
            required
          />

          <label htmlFor="email">Email</label>
          <input
            id="email"
            name="email"
            type="email"
            value={formData.email}
            onChange={handleChange}
            required
          />

          <label htmlFor="password">Password</label>
          <input
            id="password"
            name="password"
            type="password"
            value={formData.password}
            onChange={handleChange}
            minLength={8}
            autoComplete="new-password"
            required
          />

          <label htmlFor="howDidYouHear">How did you hear about us</label>
          <select
            id="howDidYouHear"
            name="howDidYouHear"
            value={formData.howDidYouHear}
            onChange={handleChange}
            required
          >
            <option value="">Select an option</option>
            {hearAboutOptions.map((option) => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
          </select>

          {Number(formData.howDidYouHear) === 18 ? (
            <>
              <label htmlFor="hearAboutOther">How did you hear about us (other)</label>
              <input
                id="hearAboutOther"
                name="hearAboutOther"
                type="text"
                value={formData.hearAboutOther}
                onChange={handleChange}
                required
              />
            </>
          ) : null}

          <label htmlFor="mainGoal">What is your main goal with Admira</label>
          <select
            id="mainGoal"
            name="mainGoal"
            value={formData.mainGoal}
            onChange={handleChange}
            required
          >
            <option value="">Select an option</option>
            {MAIN_GOAL_OPTIONS.map((option) => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
          </select>

          <div className="plans-wrapper">
            <p className="plans-title">Choose your plan</p>
            <div className="billing-toggle-wrap">
              <span className="billing-toggle-label">Billing cycle</span>
              <div className="billing-toggle" role="radiogroup" aria-label="Billing cycle">
                <button
                  type="button"
                  className={`billing-toggle-option ${
                    formData.billingCycle === 'monthly' ? 'billing-toggle-option-active' : ''
                  }`}
                  onClick={() => handleBillingCycleChange('monthly')}
                  aria-pressed={formData.billingCycle === 'monthly'}
                >
                  Monthly
                </button>
                <button
                  type="button"
                  className={`billing-toggle-option ${
                    formData.billingCycle === 'annual' ? 'billing-toggle-option-active' : ''
                  }`}
                  onClick={() => handleBillingCycleChange('annual')}
                  aria-pressed={formData.billingCycle === 'annual'}
                >
                  Annual <span className="billing-save-badge">Save 25%</span>
                </button>
              </div>
            </div>
            <div className="plans-grid">
              {PLAN_OPTIONS.map((plan) => {
                const isSelected = formData.selectedPlan === plan.id;
                const displayedPrice = getDisplayedPlanPrice(plan);
                return (
                  <article
                    key={plan.id}
                    className={`plan-card ${isSelected ? 'plan-card-selected' : ''}`}
                  >
                    {plan.popular ? <span className="popular-badge">Popular</span> : null}
                    <h3>{plan.title}</h3>
                    <p className="plan-price">
                      {displayedPrice ? (
                        <>
                          {isAnnualBilling ? (
                            <small className="plan-price-original">${plan.monthlyPrice}/month</small>
                          ) : null}
                          <span>${displayedPrice}</span>
                          <small>USD</small>
                          <small>
                            /month 
                          </small>
                        </>
                      ) : (
                        <span>Custom</span>
                      )}
                    </p>
                    {plan.id === 'enterprise' && isSelected ? (
                      <div className="enterprise-inline-pricing">
                        <div
                          className="enterprise-inline-cycle"
                          role="radiogroup"
                          aria-label="Enterprise billing cycle"
                        >
                          <button
                            type="button"
                            className={`enterprise-inline-cycle-option ${
                              formData.enterpriseBillingCycle === 'monthly'
                                ? 'enterprise-inline-cycle-option-active'
                                : ''
                            }`}
                            onClick={() => handleEnterpriseBillingCycleChange('monthly')}
                            aria-pressed={formData.enterpriseBillingCycle === 'monthly'}
                          >
                            Monthly
                          </button>
                          <button
                            type="button"
                            className={`enterprise-inline-cycle-option ${
                              formData.enterpriseBillingCycle === 'annual'
                                ? 'enterprise-inline-cycle-option-active'
                                : ''
                            }`}
                            onClick={() => handleEnterpriseBillingCycleChange('annual')}
                            aria-pressed={formData.enterpriseBillingCycle === 'annual'}
                          >
                            Annual
                          </button>
                        </div>
                        <div className="enterprise-inline-input-wrap">
                          <span>$</span>
                          <input
                            id="enterpriseMonthlyPrice"
                            name="enterpriseMonthlyPrice"
                            type="number"
                            min="1"
                            step="0.01"
                            value={formData.enterpriseMonthlyPrice}
                            onChange={handleChange}
                            placeholder="499"
                            required={isSelected}
                          />
                          <small>
                            {formData.enterpriseBillingCycle === 'annual' ? '/year' : '/month'}
                          </small>
                        </div>
                      </div>
                    ) : null}
                    <ul>
                      {plan.features.map((feature) => (
                        <li key={feature}>{feature}</li>
                      ))}
                    </ul>
                    <button
                      type="button"
                      className="plan-button"
                      onClick={() =>
                        setFormData((previous) => ({
                          ...previous,
                          selectedPlan: plan.id,
                        }))
                      }
                    >
                      {isSelected ? 'Selected' : `Choose ${plan.title}`}
                    </button>
                  </article>
                );
              })}
            </div>
          </div>

          <button type="submit" disabled={isSubmitting}>
            {isSubmitting ? 'Creating user and workspace...' : 'Create user'}
          </button>
        </form>

        {message ? <p className="status success">{message}</p> : null}
        {error ? <p className="status error">{error}</p> : null}
      </section>
    </main>
  );
}

export default App;
